import { UkDateInput, ukDate } from "@/components/UkDateInput";

import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, Download, Check, X, Send } from "lucide-react";

export const DOC_STATUS = {
  pending: ["Awaiting approval", "bg-gold-light text-gold-dark"],
  approved: ["Approved", "bg-sage-light text-sage-dark"],
  rejected: ["Not accepted", "bg-coral-light text-coral-dark"],
};
const TYPES = ["Passport / ID", "Proof of address", "PVG certificate", "SSSC certificate", "Right to work", "Share code", "Visa document", "Other"];
const inp = "rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";
export const docUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/documents/${id}/download?auth=${localStorage.getItem("bsb_token")}`;

export function StatusPill({ status }) {
  const [label, tone] = DOC_STATUS[status || "approved"] || DOC_STATUS.approved;
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>{label}</span>;
}

export function PvgApplied({ item, onChanged }) {
  const [day, setDay] = useState(new Date().toISOString().slice(0, 10));
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.post("/me/pvg-applied", { applied_date: day, reference: ref }); toast.success("Thanks — the office can see your PVG is on the way"); setEditing(false); onChanged && onChanged(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-gold/40 bg-gold-light/30 p-6 shadow-sm lg:col-span-2" data-testid="pvg-applied-card">
      <h2 className="mb-1 font-heading text-lg font-semibold text-ink">Your PVG</h2>
      {item.applied_date && !editing ? (
        <p className="text-sm text-ink-soft" data-testid="pvg-applied-status">You told us you applied on <span className="font-semibold">{ukDate(item.applied_date)}</span>. Please upload your certificate above when it arrives. <button onClick={() => { setDay(item.applied_date); setEditing(true); }} data-testid="pvg-applied-change" className="font-semibold text-brand underline">Change</button></p>
      ) : (
        <>
          <p className="mb-3 text-sm text-ink-soft">A PVG is required before you can be booked for visits. The office will send you the link to apply. Once you have applied, let us know below.</p>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-xs text-ink-muted">Date applied<UkDateInput max={new Date().toISOString().slice(0, 10)} value={day} onChange={setDay} testid="pvg-applied-date" /></label>
            <label className="text-xs text-ink-muted">Reference (optional)<input className={`${inp} block`} value={ref} onChange={(e) => setRef(e.target.value)} data-testid="pvg-applied-ref" /></label>
            <button disabled={busy || !day} onClick={save} data-testid="pvg-applied-button" className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"><Check size={14} className="mr-1 inline" />I've applied</button>
          </div>
        </>
      )}
    </div>
  );
}

export function MyDocuments({ onChanged }) {
  const [docs, setDocs] = useState([]);
  const [dtype, setDtype] = useState("Passport / ID");
  const [expiry, setExpiry] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/me/documents").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const upload = async () => {
    if (!file) { toast.error("Choose a file first"); return; }
    const fd = new FormData(); fd.append("file", file); fd.append("doc_type", dtype); fd.append("expiry_date", expiry);
    setBusy(true);
    try { await api.post("/me/documents", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Uploaded — the office will review it"); setFile(null); setExpiry(""); load(); onChanged && onChanged(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-border bg-white p-6 shadow-sm lg:col-span-2" data-testid="my-documents-upload">
      <h2 className="mb-1 flex items-center gap-2 font-heading text-lg font-semibold text-ink"><Upload size={18} className="text-brand" /> Upload a document</h2>
      <p className="mb-4 text-sm text-ink-soft">We need your Passport / ID, 2 proofs of address (e.g. a utility bill or bank statement from the last 3 months) and your PVG certificate. SSSC is optional. Each upload is checked by the office before it's accepted.</p>
      <div className="flex flex-wrap items-end gap-2">
        <select className={inp} value={dtype} onChange={(e) => setDtype(e.target.value)} data-testid="my-doc-type-select">{TYPES.map((t) => <option key={t}>{t}</option>)}</select>
        <div><label className="block text-[11px] text-ink-muted">Expiry (if any)</label><UkDateInput value={expiry} onChange={setExpiry} testid="my-doc-expiry-input" /></div>
        <label className="cursor-pointer rounded-lg border border-border bg-white px-3 py-2 text-sm text-ink hover:bg-secondary">
          <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp" capture={undefined} onChange={(e) => setFile(e.target.files[0])} data-testid="my-doc-file-input" />
          {file ? file.name.slice(0, 20) : "Choose file or photo"}
        </label>
        <button onClick={upload} disabled={busy} data-testid="my-doc-upload-button" className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-60"><Upload size={15} /> {busy ? "Uploading…" : "Upload"}</button>
      </div>
      {docs.length > 0 && (
        <div className="mt-4 space-y-2">
          {docs.map((d) => (
            <div key={d.id} data-testid={`my-doc-${d.id}`} className="flex items-center gap-3 rounded-lg border border-border p-2.5">
              <FileText size={16} className="shrink-0 text-brand" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-ink">{d.doc_type} · <span className="text-ink-muted">{d.original_filename}</span></p>
                {d.review_status === "rejected" && d.review_note && <p className="text-xs text-coral-dark">{d.review_note}</p>}
              </div>
              <StatusPill status={d.review_status} />
              <a href={docUrl(d.id)} target="_blank" rel="noreferrer" className="rounded-lg border border-border px-2 py-1 text-ink hover:bg-secondary" data-testid={`my-doc-view-${d.id}`}><Download size={14} /></a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function PendingDocs() {
  const [docs, setDocs] = useState([]);
  const load = () => api.get("/documents/pending").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const review = async (d, status) => {
    const note = status === "rejected" ? window.prompt("Reason (sent to the befriender):", "") : "";
    if (note === null) return;
    try { await api.put(`/documents/${d.id}/review`, { status, note }); toast.success(status === "approved" ? "Document approved" : "Document rejected"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remind = async () => {
    if (!window.confirm("Email every befriender their list of documents due now?")) return;
    try { await api.post("/documents/send-reminders"); toast.success("Reminder emails are being sent to befrienders with documents due"); } catch (e) { toast.error(apiError(e)); }
  };
  const preview = async () => {
    try { const { data } = await api.post("/documents/send-reminders/preview"); toast.success(data.befrienders ? `Preview of ${data.befrienders} email(s) sent to ${data.sent_to}` : "No befrienders have documents due"); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="rounded-xl border border-gold/40 bg-white p-5 shadow-sm" data-testid="pending-docs">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-lg font-semibold text-ink">Documents awaiting approval ({docs.length})</h2>
        <div className="flex gap-2">
        <button onClick={preview} data-testid="preview-doc-reminders-button" className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-ink hover:bg-secondary"><FileText size={13} /> Email me a preview</button>
        <button onClick={remind} data-testid="send-doc-reminders-button" className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-ink hover:bg-secondary"><Send size={13} /> Email reminders now</button>
        </div>
      </div>
      {docs.length === 0 ? <p className="text-sm text-ink-muted" data-testid="no-pending-docs">Nothing to review right now.</p> : (
        <div className="space-y-2">
          {docs.map((d) => (
            <div key={d.id} data-testid={`pending-doc-${d.id}`} className="flex flex-wrap items-center gap-3 rounded-lg border border-border p-2.5">
              <FileText size={16} className="shrink-0 text-brand" />
              <div className="min-w-0 flex-1"><p className="text-sm font-semibold text-ink">{d.staff_name} · {d.doc_type}</p><p className="truncate text-xs text-ink-muted">{d.original_filename}{d.expiry_date ? ` · expires ${ukDate(d.expiry_date)}` : ""}</p></div>
              <a href={docUrl(d.id)} target="_blank" rel="noreferrer" data-testid={`pending-doc-view-${d.id}`} className="rounded-lg border border-border px-2.5 py-1 text-xs font-semibold text-ink hover:bg-secondary">View</a>
              <button onClick={() => review(d, "approved")} data-testid={`approve-doc-${d.id}`} className="inline-flex items-center gap-1 rounded-lg bg-sage px-2.5 py-1 text-xs font-semibold text-white"><Check size={13} /> Approve</button>
              <button onClick={() => review(d, "rejected")} data-testid={`reject-doc-${d.id}`} className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-xs font-semibold text-coral-dark hover:bg-coral-light"><X size={13} /> Reject</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
